// PeriodicTaskEntryDialog.js
// -----------------------------------------------------------------------------
// THE single "record a periodic-task value" popup, shared by all three places
// a value can be entered:
//   • Regulatory Compliance  → per-element "Update" (was TaskEntryDialog)
//   • Head Office            → Periodic tasks grid cell click
//   • Single pharmacy        → Periodic tasks card "Record" button
// Superset of every field the three old forms had, so nothing is lost:
//   • Value        — input adapts to the task's dataType
//                    (boolean → Yes/No, choice → options, date, number, text)
//   • Month        — MONTHLY tasks: which month the value applies to
//                    (last 13 months, enabling back-entry; the backend anchors
//                    recordedAt to mid-month of the chosen period)
//   • Date         — NON-monthly tasks: the date the check was actually done
//                    (defaults to today, back-datable, never future)
//   • Notes        — optional, ≤500 chars, pre-filled from the last entry
// POSTs /api/headoffice/periodic-tasks/entries with
//   { taskId, dbName, pharmacyId, value, recordedAt?, periodKey?, note }
// (addEntry in headoffice.js accepts all of these; periodKey wins over
// recordedAt for monthly tasks, matching the old single-pharmacy recorder).
// -----------------------------------------------------------------------------
import React, { useEffect, useState } from "react";

import API_BASE from "./apiBase";

// Last 13 months as 'YYYY-MM', newest first (current month at index 0).
function monthOptions() {
  const out = [];
  const now = new Date();
  let y = now.getUTCFullYear(), m = now.getUTCMonth() + 1;
  for (let i = 0; i < 13; i++) {
    out.push(`${y}-${String(m).padStart(2, "0")}`);
    m--; if (m < 1) { m = 12; y--; }
  }
  return out;
}
const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const monthLabel = (k) => {
  const [y, m] = k.split("-").map(Number);
  return `${MONTH_NAMES[m - 1]} ${y}`;
};

/**
 * Props:
 *   token        – JWT for the entries POST
 *   task         – { taskId, description, dataType, frequency, options[] }
 *   target       – { dbName, pharmacyId, pharmacyName }
 *   title        – optional heading override (defaults to task.description)
 *   existingNote – note carried on the latest entry (pre-fills Notes)
 *   onClose()    – dismiss without saving
 *   onSaved()    – called after a successful save
 */
/**
 * existingEntry (optional) — the latest recorded entry for this task/pharmacy:
 *   { entryId, value, recordedAt, periodKey, note, recordedBy }
 * When present the dialog opens in EDIT mode: fields pre-filled, a Delete
 * button removes the entry completely, attachments on the entry are listed,
 * and Save records a fresh entry (the newest entry always drives the grid).
 */
export default function PeriodicTaskEntryDialog({
  token, task, target, title, existingNote, existingEntry = null, onClose, onSaved, onDeleted
}) {
  const todayStr = new Date().toISOString().slice(0, 10);
  const isMonthly = task.frequency === "monthly";
  const months = monthOptions();

  const [value, setValue] = useState(() => {
    if (existingEntry && existingEntry.value != null && existingEntry.value !== "") {
      if (task.dataType === "boolean")
        return (existingEntry.value === true || existingEntry.value === "yes" || existingEntry.value === "true") ? "yes" : "no";
      if (task.dataType === "date") {
        const d = new Date(existingEntry.value);
        if (!isNaN(d)) return d.toISOString().slice(0, 10);
      }
      return String(existingEntry.value);
    }
    if (task.dataType === "boolean") return "yes";
    if (task.dataType === "choice") return (task.options || [])[0] || "";
    if (task.dataType === "date") return todayStr;
    return "";
  });
  const [period, setPeriod] = useState(
    existingEntry?.periodKey && monthOptions().includes(existingEntry.periodKey)
      ? existingEntry.periodKey : months[0]);
  const [dateStr, setDateStr] = useState(
    existingEntry?.recordedAt ? new Date(existingEntry.recordedAt).toISOString().slice(0, 10) : todayStr);
  const [note, setNote] = useState(existingEntry?.note ?? existingNote ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // "Entered by" — active staff at the target pharmacy.
  const [staffList, setStaffList] = useState([]);
  const [enteredBy, setEnteredBy] = useState(existingEntry?.recordedBy || "");
  useEffect(() => {
    let cancelled = false;
    if (!target?.dbName) return;
    fetch(`${API_BASE}/api/headoffice/periodic-tasks/staff?dbName=${encodeURIComponent(target.dbName)}`,
          { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(j => { if (!cancelled) setStaffList(j.staff || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [target?.dbName, token]);

  // Attachment: new upload + any already on the existing entry.
  const [file, setFile] = useState(null);
  const [attachments, setAttachments] = useState([]);
  useEffect(() => {
    let cancelled = false;
    if (!existingEntry?.entryId) return;
    fetch(`${API_BASE}/api/headoffice/periodic-tasks/entries/attachments?entryId=${existingEntry.entryId}`,
          { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(j => { if (!cancelled) setAttachments(j.attachments || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [existingEntry?.entryId, token]);

  const deleteEntry = async () => {
    if (!existingEntry?.entryId) return;
    if (!window.confirm("Delete this entry completely? The task will revert to its previous entry (or show as never recorded).")) return;
    setBusy(true); setErr("");
    try {
      const res = await fetch(`${API_BASE}/api/headoffice/periodic-tasks/entries/delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ entryId: existingEntry.entryId })
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.error || `HTTP ${res.status}`); }
      (onDeleted || onSaved) && (onDeleted || onSaved)();
    } catch (e) { setErr(String(e.message || e)); setBusy(false); }
  };

  const canSave = !busy && dateStr &&
    !(task.dataType === "numeric" && value === "") &&
    !(task.dataType === "date" && !value) &&
    !(task.dataType === "choice" && value === "");

  const save = async () => {
    setBusy(true); setErr("");
    try {
      let v = value;
      if (task.dataType === "boolean") v = value === "yes";
      if (task.dataType === "numeric") v = Number(value);
      const body = {
        taskId: String(task.taskId),
        dbName: target.dbName,
        pharmacyId: target.pharmacyId != null ? String(target.pharmacyId) : null,
        value: v,
        note: note.trim() || null
      };
      if (isMonthly) {
        body.periodKey = period;                 // backend anchors recordedAt mid-month
      } else {
        // Chosen date at midday local so it lands on the right day in every
        // timezone rendering (same convention as the old compliance dialog).
        body.recordedAt = new Date(`${dateStr}T12:00:00`).toISOString();
      }
      if (enteredBy) body.recordedBy = enteredBy;
      const res = await fetch(`${API_BASE}/api/headoffice/periodic-tasks/entries`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try { const j = await res.json(); if (j?.error) msg = j.error; } catch (_) {}
        throw new Error(msg);
      }
      // Attach the chosen file to the entry just created.
      if (file) {
        try {
          const saved = await res.clone().json().catch(() => null) || await res.json();
          const b64 = await new Promise((resv, rej) => {
            const fr = new FileReader();
            fr.onload = () => resv(String(fr.result).split(",")[1]);
            fr.onerror = rej; fr.readAsDataURL(file);
          });
          await fetch(`${API_BASE}/api/headoffice/periodic-tasks/entries/attachment`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ entryId: String(saved._id), filename: file.name,
                                   mimeType: file.type, dataBase64: b64 })
          });
        } catch (e) { console.error("attachment upload failed:", e); }
      }
      onSaved && onSaved();
    } catch (e) { setErr(String(e.message || e)); setBusy(false); }
  };

  const inputStyle = { padding: "7px 10px", fontSize: 13, border: "1px solid #cbd5e1",
                       borderRadius: 6, width: "100%", boxSizing: "border-box", outline: "none" };
  const labelStyle = { display: "block", fontSize: 11, fontWeight: 700, color: "#475569",
                       textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 };

  return (
    <div onClick={onClose}
         style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)",
                  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1200 }}>
      <div onClick={(e) => e.stopPropagation()}
           style={{ width: "100%", maxWidth: 400, background: "#fff", borderRadius: 10,
                    boxShadow: "0 12px 40px rgba(0,0,0,0.3)", padding: 18,
                    maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#0f172a", marginBottom: 2 }}>
          {title || `Update: ${task.description}`}
        </div>
        <div style={{ fontSize: 12, color: "#64748b", marginBottom: 12 }}>
          {target.pharmacyName || ""}
          {task.frequency ? (
            <span style={{ marginLeft: target.pharmacyName ? 8 : 0, textTransform: "capitalize",
                           background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 4,
                           padding: "1px 6px", fontSize: 11 }}>
              {task.frequency}
            </span>
          ) : null}
        </div>

        <label style={labelStyle}>Value</label>
        {task.dataType === "boolean" ? (
          <select value={value} onChange={(e) => setValue(e.target.value)} style={inputStyle}>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        ) : task.dataType === "choice" ? (
          <select value={value} onChange={(e) => setValue(e.target.value)} style={inputStyle}>
            {(task.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        ) : task.dataType === "date" ? (
          <input type="date" value={value} onChange={(e) => setValue(e.target.value)} style={inputStyle} />
        ) : task.dataType === "numeric" ? (
          <input type="number" value={value} onChange={(e) => setValue(e.target.value)} style={inputStyle} />
        ) : (
          <input type="text" value={value} onChange={(e) => setValue(e.target.value)} style={inputStyle} />
        )}

        {isMonthly ? (
          <>
            <label style={{ ...labelStyle, margin: "12px 0 4px" }}>Month this value applies to</label>
            <select value={period} onChange={(e) => setPeriod(e.target.value)} style={inputStyle}
                    title="Lets you enter or correct figures for previous months.">
              {months.map(k => <option key={k} value={k}>{monthLabel(k)}</option>)}
            </select>
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
              Defaults to the current month; pick an earlier month to back-enter a figure.
            </div>
          </>
        ) : (
          <>
            <label style={{ ...labelStyle, margin: "12px 0 4px" }}>Date recorded</label>
            <input type="date" value={dateStr} max={todayStr}
                   onChange={(e) => setDateStr(e.target.value)} style={inputStyle} />
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
              Defaults to today; set an earlier date if the check was actually done then.
            </div>
          </>
        )}

        <label style={{ ...labelStyle, margin: "12px 0 4px" }}>Entered by</label>
        <select value={enteredBy} onChange={(e) => setEnteredBy(e.target.value)} style={inputStyle}>
          <option value="">— select staff member —</option>
          {staffList.map(st => <option key={st.staffId} value={st.name}>{st.name}</option>)}
          {enteredBy && !staffList.some(st => st.name === enteredBy) && (
            <option value={enteredBy}>{enteredBy}</option>
          )}
        </select>

        <label style={{ ...labelStyle, margin: "12px 0 4px" }}>Notes (optional)</label>
        <textarea value={note} maxLength={500} rows={3}
                  placeholder={"e.g. Checked against the paper RP log kept at the counter"}
                  onChange={(e) => setNote(e.target.value)}
                  style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }} />
        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
          Carried on this entry and shown with the item — pre-filled from the
          last entry, so it can be kept or updated each time.
        </div>

        <label style={{ ...labelStyle, margin: "12px 0 4px" }}>Attach document / photo (optional)</label>
        <input type="file" accept="image/*,.pdf,.doc,.docx" onChange={(e) => setFile(e.target.files?.[0] || null)}
               style={{ fontSize: 12, width: "100%" }} />
        {attachments.length > 0 && (
          <div style={{ marginTop: 6, fontSize: 12 }}>
            {attachments.map(a => (
              <div key={a.attachmentId}>
                📎 <a href={`${API_BASE}/api/headoffice/periodic-tasks/attachment-file?id=${a.attachmentId}`}
                      target="_blank" rel="noreferrer" style={{ color: "#1d4ed8" }}>{a.filename}</a>
                <span style={{ color: "#94a3b8" }}> ({Math.round((a.size || 0) / 1024)} KB{a.uploadedBy ? ` · ${a.uploadedBy}` : ""})</span>
              </div>
            ))}
          </div>
        )}

        {err && <div style={{ fontSize: 12, color: "#b91c1c", marginTop: 10 }}>{err}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
          {existingEntry?.entryId && (
            <button onClick={deleteEntry} disabled={busy}
                    style={{ marginRight: "auto", padding: "7px 14px", fontSize: 13,
                             border: "1px solid #fca5a5", background: "#fef2f2", color: "#b91c1c",
                             fontWeight: 700, borderRadius: 6, cursor: "pointer" }}>
              Delete entry
            </button>
          )}
          <button onClick={onClose} disabled={busy}
                  style={{ padding: "7px 14px", fontSize: 13, border: "1px solid #cbd5e1",
                           background: "#fff", color: "#475569", borderRadius: 6, cursor: "pointer" }}>Cancel</button>
          <button onClick={save} disabled={!canSave}
                  style={{ padding: "7px 14px", fontSize: 13, border: "none", fontWeight: 700,
                           background: busy ? "#93c5fd" : "#1d4ed8", color: "#fff", borderRadius: 6,
                           cursor: canSave ? "pointer" : "default", opacity: canSave ? 1 : 0.7 }}>
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}